import React, { useEffect, useMemo, useState } from 'react'
import { Col, DropdownButton, Grid, MenuItem, Row } from 'react-bootstrap'
import axios from 'axios'

import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useT } from '@transifex/react'
import MyLoader from '../common/components/MyLoader'
import { fetchCollection } from '../common/redux/collectionActions'
import { dateFromObjectId } from '../common/utils'
import InviteCarrierModal from './InviteCarrier/InviteCarrierModal'
import Lock from './Lock/components/Lock'
import LockEvents from './Lock/components/LockEvents'
import MessageGroupsModal from './MessageGroups/MessageGroupsModal'
import SelectedAviseringLockModal from './MessageGroups/SelectedAviseringLockModal'
import NewEasyAccessModal from './Modals/NewEasyAccessModal'
import NewUserModal from './Modals/NewUserModal'
import CarrierContainer from './Carrier/CarrierContainer'
import VendorView from './VendorView'
import QTextInputField from '../common/components/QTextInputField'
import useSWR from '../hooks/useSWR'
import useThrottledState from '../hooks/useThrottledState'

const Dash = () => {
  const t = useT()
  const admin = useSelector((state) => state.collectionData.admin)
  const locks = useSelector((state) => state.collectionData.locks)
  const controllers = useSelector((state) => state.collectionData.controllers)
  const isLoading = useSelector((state) => state.collectionData.isLoading)
  const rentalCompanies = useSelector((state) => state.collectionData.rental_companies)
  const keyedLocks = useSelector((state) => state.collectionData.keyedLocks)
  const dispatch = useDispatch()
  const history = useHistory()

  const [lockSorting, setLockSorting] = useState('Alphabetically')
  const [search, setSearch] = useThrottledState('')
  const [showNewUserModal, setShowNewUserModal] = useState(false)
  const [isExportingUnits, setIsExportingUnits] = useState(false)
  const [showInviteCarrierModal, setShowInviteCarrierModal] = useState(false)
  const [showEasyAccessModal, setShowEasyAccessModal] = useState(false)
  const [showSelectedAviseringLockModal, setShowSelectedAviseringLockModal] = useState(false)
  const [selectedLock, setSelectedLock] = useState(null)
  const [aviseringModalLockId, setAviseringModalLockId] = useState(null)
  const [eligibleAviseringLocks, setEligibleAviseringLocks] = useState([])
  const { data: filteredLockData } = useSWR(search.length > 2 && `/subject/admin/locks/search?search=${search}`)

  const isMobile = window.innerWidth < 768

  useEffect(() => {
    dispatch(fetchCollection('locks'))
    dispatch(fetchCollection('controllers'))
    dispatch(fetchCollection('events'))
    dispatch(fetchCollection('rental_companies'))
  }, [])

  const usertype = admin?.type

  const openNewUserModal = (lock) => {
    setSelectedLock(lock)
    setShowNewUserModal(true)
  }

  const openSelectedAviseringLockModal = (lock) => {
    const eligibleLocks = locks.filter((l) => l.sub_type === 'container' && l.isCarrierDoor !== true)

    if (eligibleLocks.length === 1) {
      return setAviseringModalLockId(lock?._id || eligibleLocks[0]._id)
    }

    setEligibleAviseringLocks(eligibleLocks)
    setShowSelectedAviseringLockModal(true)
  }

  const exportUnits = () => {
    setIsExportingUnits(true)
    axios
      .get(`${window.MYQLOCX_API}/subject/admin/statistics/units.xlsx`)
      .then(({ data }) => window.open(data.fileUrl, '_blank'))
      .finally(() => setIsExportingUnits(false))
  }

  const generateTopButtons = () => {
    const hasLocks = admin?.user?.locks?.length

    const buttons = []

    if (hasLocks) {
      buttons.push(
        <div style={{ textAlign: 'center', borderRadius: 20 }} className="AddUser" onClick={() => openNewUserModal()}>
          {t('+ New User')}
        </div>
      )
      if (locks.some((l) => l.sub_type !== 'container' && !l.storage && l.controller)) {
        buttons.push(
          <div style={{ textAlign: 'center', borderRadius: 20 }} className="AddUser" onClick={() => setShowEasyAccessModal(true)}>
            {t('+ Share Guest Key')}
          </div>
        )
      }
    }

    if (hasLocks && locks.filter((l) => l.type === 'box' || l.type === 'room').length) {
      buttons.push(
        <div style={{ textAlign: 'center', borderRadius: 20 }} className="AddUser" onClick={() => openSelectedAviseringLockModal()}>
          {t('+ Notification Settings')}
        </div>
      )
      buttons.push(
        <div style={{ textAlign: 'center', borderRadius: 20 }} className="AddUser" onClick={() => setShowInviteCarrierModal(true)}>
          {t('+ Local Drivers')}
        </div>
      )
    }

    return buttons.map((b, i) => (
      <Col key={i} xs={12} md={Math.floor(12 / buttons.length)} lg={Math.floor(12 / buttons.length)}>
        {b}
      </Col>
    ))
  }

  const sortedLocks = useMemo(
    () =>
      [...locks].sort((a, b) => {
        if (lockSorting === 'Alphabetically') {
          return a.custom_name > b.custom_name ? 1 : -1
        }
        if (lockSorting === 'Created') {
          return dateFromObjectId(b._id) - dateFromObjectId(a._id)
        }

        return 0
      }),

    [lockSorting, locks]
  )

  const sortedFilteredLocks = useMemo(() => {
    if (!filteredLockData) return null

    return filteredLockData
      .map((l) => keyedLocks[l])
      .sort((a, b) => {
        if (lockSorting === 'Alphabetically') {
          return a.custom_name > b.custom_name ? 1 : -1
        }
        if (lockSorting === 'Created') {
          return dateFromObjectId(b._id) - dateFromObjectId(a._id)
        }

        return 0
      })
  }, [lockSorting, filteredLockData])

  if (admin?.type === 'vendor') {
    return <VendorView admin={admin} />
  }

  if (admin?.type === 'carrier') {
    return <CarrierContainer />
  }

  return (
    <div className="dash-container">
      <MyLoader />
      {showNewUserModal && <NewUserModal locks={locks} predefinedSubject={{}} modalIsOpen={true} closeModal={() => setShowNewUserModal(false)} rentalCompanies={rentalCompanies} />}
      {showSelectedAviseringLockModal && (
        <SelectedAviseringLockModal
          selectedLock={(lock) => {
            setShowSelectedAviseringLockModal(false)
            setAviseringModalLockId(lock._id)
          }}
          eligibleAviseringLocks={eligibleAviseringLocks}
          closeModal={() => setShowSelectedAviseringLockModal(false)}
        />
      )}
      {aviseringModalLockId && <MessageGroupsModal lockId={aviseringModalLockId} closeModal={() => setAviseringModalLockId(null)} />}
      {showInviteCarrierModal && <InviteCarrierModal closeModal={() => setShowInviteCarrierModal(false)} />}
      {showEasyAccessModal && <NewEasyAccessModal locks={locks} closeModal={() => setShowEasyAccessModal(false)} />}
      <Grid style={{ width: '90%' }}>
        <Row>{generateTopButtons()}</Row>
      </Grid>
      <div className="dash-content">
        {usertype === 'admin' && !isLoading && (
          <div style={{ marginTop: '30px' }}>
            <Grid className="component-width">
              <Row className="show-grid">
                <Col md={2}>
                  <h3 className="sub-title">
                    {t('My Units')} ({locks.length})
                  </h3>
                </Col>
                <Col md={2}>
                  {!isMobile && (
                    <DropdownButton className="delivery-filter-container" title={t(lockSorting || 'Sort By')} id="dropdown-size-medium">
                      <div style={{ width: '200px' }}>
                        <MenuItem eventKey="Alphabetically" onSelect={(eventKey) => setLockSorting(eventKey)}>
                          <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{t('Alphabetically')}</div>
                        </MenuItem>
                        <MenuItem eventKey="Created" onSelect={(eventKey) => setLockSorting(eventKey)}>
                          <div style={{ marginLeft: '5px', textAlign: 'left', color: 'black' }}>{t('Created')}</div>
                        </MenuItem>
                      </div>
                    </DropdownButton>
                  )}
                </Col>
                {locks.length > 0 && (
                  <Col xs={4} md={12} lg={4}>
                    <QTextInputField onChange={(e) => setSearch(e.target.value)} />
                  </Col>
                )}
                {(admin.user.email === 'storageqlocx' || admin.user.email === 'hiltistorage') && (
                  <>
                    <Col md={2}>&nbsp;</Col>
                    <Col md={2}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button style={{ padding: 10, borderRadius: 10, border: '1px solid #afafaf', background: 'white', color: '#006e78' }} disabled={isExportingUnits} onClick={exportUnits}>
                          {isExportingUnits ? t('Exporting...') : t('Export units')}
                        </button>
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </Grid>

            <Grid className="component-width">{!sortedFilteredLocks && sortedLocks.map((lock) => <Lock key={lock._id} history={history} controllers={controllers} lock={lock} isMobile={isMobile} setGuestAsLockUser={openNewUserModal} />)}</Grid>
            <Grid className="component-width">{sortedFilteredLocks && sortedFilteredLocks.map((lock) => <Lock key={lock._id} history={history} controllers={controllers} lock={lock} isMobile={isMobile} setGuestAsLockUser={openNewUserModal} />)}</Grid>

            <Grid className="component-width">
              <LockEvents filteredLockData={filteredLockData} />
            </Grid>
          </div>
        )}
      </div>
    </div>
  )
}

export default Dash
